// The contextual inspector: controls for the selected layer (text / image / shape /
// logo / overlay), plus common layer props. Free-canvas, but seeded on-brand: color
// pickers lead with the brand palette.

import { ImageIcon } from 'lucide-react'
import type { CompositionEditor } from './use-composition-editor'
import type { DecorLayer, Fill, ImageLayer, ShapeLayer, TextLayer } from '../types'
import { ColorField, Field, NumberSlider, Section, Segmented } from './controls'
import { ReplacePhotoPopover } from './ReplacePhotoPopover'
import { DECOR_MARKS, decorUri } from '../decor'
import { FontPicker } from './FontPicker'
import { groupRunsIntoLines, readableTextColor } from '../render-helpers'
import { LOGICAL_FONT_FAMILIES } from '../use-composition-fonts'
import { brandAccent } from './composition-ops'

// The brand's REAL recorded faces (from brand.fonts, plus the layer's current
// family so a picked font never vanishes from its own menu), pinned first.
function brandFontFamilies(editor: CompositionEditor, current: string): string[] {
  const fonts = editor.comp.brand.fonts
  return [...new Set([fonts.display, fonts.body, current])].filter(
    (f): f is string => !!f && !LOGICAL_FONT_FAMILIES.has(f)
  )
}

/** True when NO face in this layer can render a real italic: the vendored logical faces
 *  ship no italic cut, and neither satori nor the canvas synthesizes one — the toggle
 *  would be an honest no-op, so it's disabled instead. */
export function italicUnavailable(layer: TextLayer): boolean {
  const families = [layer.fontFamily, ...layer.runs.map(r => r.fontFamily ?? layer.fontFamily)]
  return families.every(f => LOGICAL_FONT_FAMILIES.has(f))
}

function brandPalette(editor: CompositionEditor): string[] {
  const p = editor.comp.brand.palette
  return [
    p.primary,
    p.secondary,
    p.accent,
    p.neutralLight,
    p.neutralDark,
    '#FFFFFF',
    '#000000',
  ].filter((c): c is string => !!c)
}

function solidColorOf(fill: Fill): string {
  if (fill.kind === 'solid') return fill.color
  if (fill.kind === 'gradient') return fill.from
  if (fill.kind === 'rgba' && !fill.color.startsWith('radial:')) return '#000000'
  return '#000000'
}

export function Inspector({ editor }: { editor: CompositionEditor }) {
  const { selected } = editor
  if (!selected) {
    return (
      <div className="px-4 py-6 text-sm text-paper-faint">
        Select a layer on the canvas to edit it, or add a new one.
      </div>
    )
  }
  const palette = brandPalette(editor)

  return (
    <div className="flex flex-col">
      {selected.type === 'text' && (
        <TextInspector editor={editor} layer={selected} palette={palette} />
      )}
      {selected.type === 'image' && <ImageInspector editor={editor} layer={selected} />}
      {selected.type === 'logo' && <LogoInspector editor={editor} />}
      {selected.type === 'decor' && (
        <DecorInspector editor={editor} layer={selected} palette={palette} />
      )}
      {(selected.type === 'shape' || selected.type === 'overlay') && (
        <FillInspector editor={editor} layer={selected} palette={palette} />
      )}

      <Section title="Layer">
        <Field label="Opacity">
          <NumberSlider
            value={Math.round((selected.opacity ?? 1) * 100)}
            min={0}
            max={100}
            onChange={v => editor.updateLayer(selected.id, { opacity: v / 100 })}
            format={v => `${v}%`}
          />
        </Field>
        <Field label="Rotation">
          <NumberSlider
            value={Math.round(selected.rotation ?? 0)}
            min={-180}
            max={180}
            onChange={v => editor.updateLayer(selected.id, { rotation: v })}
            format={v => `${v}°`}
          />
        </Field>
      </Section>
    </div>
  )
}

function TextInspector({
  editor,
  layer,
  palette,
}: {
  editor: CompositionEditor
  layer: TextLayer
  palette: string[]
}) {
  // One textarea line per VISUAL line: inline runs share a line (joined ''), lines split by '\n'.
  // Matches groupRunsIntoLines so setTextPreservingRuns maps each edited line onto the right run-line.
  const text = groupRunsIntoLines(layer.runs)
    .map(line => line.map(r => r.text).join(''))
    .join('\n')
  const multiRun = groupRunsIntoLines(layer.runs).length > 1
  return (
    <Section title="Text">
      <Field label="Content">
        <textarea
          value={text}
          onChange={e => editor.setTextContent(layer.id, e.target.value)}
          rows={3}
          className="resize-none rounded-md border border-line bg-bg-3 px-2.5 py-2 text-sm text-paper focus:border-line-bright focus:outline-none"
        />
      </Field>
      {multiRun && (
        <div className="-mt-1 text-[11px] text-paper-faint">
          Each line keeps its own style. Edit a line to change its words.
        </div>
      )}
      <Field label="Font">
        <FontPicker
          value={layer.fontFamily}
          brandFamilies={brandFontFamilies(editor, layer.fontFamily)}
          onChange={v => editor.setTextFontFamily(layer.id, v)}
        />
      </Field>
      <Field label="Size">
        <NumberSlider
          value={Math.round(layer.fontSizePct * 1000)}
          min={10}
          max={260}
          onChange={v => editor.setTextSize(layer.id, v / 1000)}
          format={v => `${(v / 10).toFixed(0)}`}
        />
      </Field>
      <Field label="Weight">
        <Segmented
          value={String(layer.fontWeight)}
          options={[
            { value: '400', label: 'Regular' },
            { value: '600', label: 'Medium' },
            { value: '700', label: 'Bold' },
          ]}
          onChange={v => editor.setTextWeight(layer.id, Number(v))}
        />
      </Field>
      <Field label="Align">
        <Segmented
          value={layer.align}
          options={[
            { value: 'left', label: 'L' },
            { value: 'center', label: 'C' },
            { value: 'right', label: 'R' },
          ]}
          onChange={v => editor.updateTextLayer(layer.id, { align: v })}
        />
      </Field>
      <Field label="Color">
        <ColorField
          value={layer.runs[0]?.color ?? layer.color}
          palette={palette}
          onChange={hex => editor.setTextColor(layer.id, hex)}
        />
      </Field>
      <Field label="Case">
        <Segmented
          value={layer.uppercase ? 'upper' : 'normal'}
          options={[
            { value: 'normal', label: 'Aa' },
            { value: 'upper', label: 'AA' },
          ]}
          onChange={v => editor.updateTextLayer(layer.id, { uppercase: v === 'upper' })}
        />
      </Field>
      <Field label="Style">
        <TreatmentToggles editor={editor} layer={layer} />
      </Field>
    </Section>
  )
}

// Italic / underline / highlight toggles for the WHOLE layer (span-level styling lives in the
// floating toolbar's selection scope). Underline + highlight draw in the brand accent pen;
// toggling highlight also flips the run text to the readable-on-accent color (the engine's
// contract — a lime bar under lime text is unreadable).
function TreatmentToggles({ editor, layer }: { editor: CompositionEditor; layer: TextLayer }) {
  const p = editor.comp.brand.palette
  const accent = brandAccent(editor.comp)
  const light = p.neutralLight ?? '#FAFAF7'
  const dark = p.neutralDark ?? '#0B0B0C'
  const italic = layer.runs.some(r => r.italic)
  const underline = layer.runs.some(r => r.underlineColor)
  const highlight = layer.runs.some(r => r.highlightColor)
  const noItalic = italicUnavailable(layer)
  const btn = (active: boolean) =>
    `flex h-8 flex-1 items-center justify-center rounded-md border text-sm transition-colors disabled:opacity-30 ${
      active
        ? 'border-line-bright bg-bg-3 text-paper'
        : 'border-line bg-bg-3/50 text-paper-dim hover:text-paper'
    }`
  return (
    <div className="flex gap-1.5">
      <button
        type="button"
        title={noItalic ? 'Pick a brand font for true italics' : 'Italic'}
        disabled={noItalic}
        className={btn(italic)}
        onClick={() => editor.setTextTreatment(layer.id, { italic: !italic })}
      >
        <span className="italic">I</span>
      </button>
      <button
        type="button"
        title="Underline (brand accent)"
        className={btn(underline)}
        onClick={() =>
          editor.setTextTreatment(layer.id, { underlineColor: underline ? null : accent })
        }
      >
        <span className="underline decoration-2">U</span>
      </button>
      <button
        type="button"
        title="Highlight bar (brand accent)"
        className={btn(highlight)}
        onClick={() => {
          if (highlight) editor.setTextTreatment(layer.id, { highlightColor: null })
          else {
            editor.setTextTreatment(layer.id, { highlightColor: accent })
            editor.setTextColor(layer.id, readableTextColor(accent, light, dark))
          }
        }}
      >
        <span className="rounded-sm bg-acid px-1 text-bg">H</span>
      </button>
    </div>
  )
}

// The accent-mark inspector: swap the mark, recolor the pen. Marks preview in their real
// stroke (regenerated data-uri SVGs, same as the canvas + master).
function DecorInspector({
  editor,
  layer,
  palette,
}: {
  editor: CompositionEditor
  layer: DecorLayer
  palette: string[]
}) {
  return (
    <Section title="Accent mark">
      <Field label="Mark">
        <div className="flex gap-1.5">
          {DECOR_MARKS.map(mark => (
            <button
              key={mark}
              type="button"
              title={mark}
              onClick={() => editor.updateLayer(layer.id, { mark } as Partial<DecorLayer>)}
              className={`flex h-9 flex-1 items-center justify-center rounded-md border p-1 transition-colors ${
                layer.mark === mark
                  ? 'border-line-bright bg-bg-3'
                  : 'border-line bg-bg-3/50 hover:border-line-bright'
              }`}
            >
              <img
                src={decorUri(mark, layer.color)}
                alt={mark}
                className="max-h-full max-w-full"
                draggable={false}
              />
            </button>
          ))}
        </div>
      </Field>
      <Field label="Color">
        <ColorField
          value={layer.color}
          palette={palette}
          onChange={hex => editor.updateLayer(layer.id, { color: hex } as Partial<DecorLayer>)}
        />
      </Field>
    </Section>
  )
}

function ImageInspector({ editor, layer }: { editor: CompositionEditor; layer: ImageLayer }) {
  return (
    <Section title="Image">
      <Field label="Photo">
        <ReplacePhotoPopover
          onPick={attachmentId => editor.replaceImageSrc(layer.id, attachmentId)}
        >
          <button
            type="button"
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-line bg-bg-3 px-2.5 py-2 text-sm text-paper transition-colors hover:border-line-bright"
          >
            <ImageIcon size={14} />
            Replace photo
          </button>
        </ReplacePhotoPopover>
      </Field>
      <Field label="Fit">
        <Segmented
          value={layer.objectFit}
          options={[
            { value: 'cover', label: 'Cover' },
            { value: 'contain', label: 'Contain' },
          ]}
          onChange={v => editor.updateLayer(layer.id, { objectFit: v } as Partial<ImageLayer>)}
        />
      </Field>
      <Field label="Reposition X">
        <NumberSlider
          value={Math.round((layer.focalX ?? 0.5) * 100)}
          min={0}
          max={100}
          onChange={v => editor.updateLayer(layer.id, { focalX: v / 100 } as Partial<ImageLayer>)}
          format={v => `${v}%`}
        />
      </Field>
      <Field label="Reposition Y">
        <NumberSlider
          value={Math.round((layer.focalY ?? 0.5) * 100)}
          min={0}
          max={100}
          onChange={v => editor.updateLayer(layer.id, { focalY: v / 100 } as Partial<ImageLayer>)}
          format={v => `${v}%`}
        />
      </Field>
      <Field label="Corner radius">
        <NumberSlider
          value={Math.round((layer.radiusPct ?? 0) * 100)}
          min={0}
          max={50}
          onChange={v =>
            editor.updateLayer(layer.id, { radiusPct: v / 100 } as Partial<ImageLayer>)
          }
          format={v => `${v}%`}
        />
      </Field>
    </Section>
  )
}

function LogoInspector({ editor }: { editor: CompositionEditor }) {
  return (
    <Section title="Logo">
      <div className="text-[11px] text-paper-faint">
        Drag to move, or resize with the handles. Hide it from the Layers panel.
      </div>
      <button
        type="button"
        onClick={() => editor.selected && editor.toggleHidden(editor.selected.id)}
        className="self-start rounded-md border border-line bg-bg-3 px-2.5 py-1.5 text-xs text-paper-dim hover:text-paper"
      >
        Toggle visibility
      </button>
    </Section>
  )
}

function FillInspector({
  editor,
  layer,
  palette,
}: {
  editor: CompositionEditor
  layer: ShapeLayer | { type: 'overlay'; id: string; fill: Fill }
  palette: string[]
}) {
  const isShape = layer.type === 'shape'
  return (
    <Section title={isShape ? 'Shape' : 'Overlay'}>
      <Field label="Color">
        <ColorField
          value={solidColorOf(layer.fill)}
          palette={palette}
          onChange={hex => editor.setLayerFill(layer.id, { kind: 'solid', color: hex })}
        />
      </Field>
      {isShape && (
        <Field label="Shape">
          <Segmented
            value={(layer as ShapeLayer).shapeKind}
            options={[
              { value: 'rect', label: 'Rect' },
              { value: 'pill', label: 'Pill' },
              { value: 'ellipse', label: 'Oval' },
              { value: 'line', label: 'Line' },
            ]}
            onChange={v => editor.updateLayer(layer.id, { shapeKind: v } as Partial<ShapeLayer>)}
          />
        </Field>
      )}
    </Section>
  )
}
